'use client';
import { useTimePilotT } from "@/i18n/useTimePilotT";

import ReactMarkdown from 'react-markdown';
import { markdownRehypeSanitizePlugins } from '@/lib/markdown-rehype-plugins';
import { SpinnerGapIcon, XIcon } from '@phosphor-icons/react';
import { mobileUserFacingErrorMessage } from '@/shared/mobile-service/api/cors-mobile-api';
import { SlideOverLayer } from '../SlideOver';

/**
 * Slide-over for one complete artifact. The row click hydrates the artifact in
 * markdown mode rather than formatting a partial field selection.
 */
export function CitedRowMarkdownPane({
  markdown,
  isLoading,
  error,
  title,
  onRetry,
  onClose,
}: {
  markdown: string | null;
  isLoading: boolean;
  error: Error | null;
  title: string;
  onRetry: () => void;
  onClose: () => void;
}) {
  const t = useTimePilotT();
  return (
    <SlideOverLayer onClose={onClose}>
      <aside
        data-testid="CitationCitedTableMarkdown"
        className="flex h-full w-full max-w-xl flex-col border-l border-white/10 bg-[#0f1d32] shadow-xl ring-1 ring-white/10"
      >
        <header className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <h2 className="m-0 truncate text-sm font-semibold text-white">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-white/50 hover:bg-white/10 hover:text-white/70"
            aria-label="Close"
          >
            <XIcon size={18} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-4 text-[12px] text-white/90">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-white/65">
              <SpinnerGapIcon size={18} className="animate-spin-only" />
              <span>{t("viewers.loadingFullArtifact")}</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="m-0 text-white/70">{mobileUserFacingErrorMessage(error)}</p>
              <button
                type="button"
                onClick={onRetry}
                className="rounded bg-brand-blue px-3 py-1.5 text-sm text-white hover:bg-brand-blue-dark"
              >
                Retry
              </button>
            </div>
          ) : (
            <ReactMarkdown
              rehypePlugins={markdownRehypeSanitizePlugins}
              components={{
                h1: ({ children }) => (
                  <h1 className="m-0 pb-2 text-[14px] font-semibold text-white">{children}</h1>
                ),
                p: ({ children }) => (
                  <p className="m-0 pb-1.5 text-[12px] font-light leading-snug text-white/90">
                    {children}
                  </p>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold text-white">{children}</strong>
                ),
              }}
            >
              {markdown ?? ''}
            </ReactMarkdown>
          )}
        </div>
      </aside>
    </SlideOverLayer>
  );
}
